import React, { Component, createRef } from "react";
import { connect } from "react-redux";
import { hideModal } from "../../redux/actions/modalActions";
import { saveNewSearch } from "../../redux/actions/searchActions";
import { saveNewCategoryFilterSelection } from "../../redux/actions/categoryAction";
import { toggleHalfThePriceProducts } from "../../redux/actions/itemActions";
import { fetchSearchSuggestions } from "../../clients/shopify/shopifyClient";
import { searchHasValue } from "../../utils/searchFunctions";
import { resetCss } from "../../utils/modal";

interface Props {
  marginTop: number;
  hideModal: any;
  saveNewSearch: any;
  saveNewCategoryFilterSelection: any;
  toggleHalfThePriceProducts: any;
  search: any;
}
interface State {
  searchTerm: string;
  items: { id: string; name: string; type?: string; url?: string; image?: string }[];
  showSuggestions: boolean;
}

class NavBarSearchMobile extends Component<Props, State> {
  private searchContainerRef = createRef<HTMLDivElement>();
  private suggestionsRef = createRef<HTMLDivElement>();
  
  state = {
    searchTerm: "",
    items: [],
    showSuggestions: false
  };

  submitSearchTerm = async (query?: string) => {
    let value = query ? query : this.state.searchTerm;

    // Hide suggestions
    this.setState({ showSuggestions: false });
    
    // Save search and navigate
    await this.props.saveNewSearch(value);
    await this.props.toggleHalfThePriceProducts(false, false);
    
    if (!searchHasValue(this.props.search)) {
      await this.props.saveNewCategoryFilterSelection(null);
    }
    
    // Navigate to search results
    window.location.href = `/browse?s=${encodeURIComponent(value)}`;
  };

  searchKeyStrokeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchTerm: e.currentTarget.value });
  };

  onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const characterCode = e.charCode || e.keyCode || e.key;
    // if user presses enter
    if (characterCode === 13) {
      // @ts-ignore
      this.submitSearchTerm(e.target.value);
    }
  };

  debounce = (func: Function, timeout = 500) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  };

  searchApi = this.debounce(async (query: string) => {
    try {
      // Use Shopify's predictive search API instead of the local backend
      const suggestions = await fetchSearchSuggestions(query);
      
      if (suggestions.products.length > 0 || suggestions.collections.length > 0 || suggestions.queries.length > 0) {
        // Format suggestions for display
        const formattedSuggestions = [
          ...suggestions.products.map(product => ({
            id: product.id,
            name: product.title,
            type: 'product',
            url: `/browse?s=${encodeURIComponent(product.title)}`,
            image: product.image || ''
          })),
          ...suggestions.collections.map(collection => ({
            id: collection.id,
            name: collection.title,
            type: 'collection',
            url: `/browse/${collection.handle}`
          })),
          ...suggestions.queries.map(query => ({
            id: `query-${query}`,
            name: query,
            type: 'query',
            url: `/browse?s=${encodeURIComponent(query)}`
          }))
        ];
        
        this.setState({ items: formattedSuggestions, searchTerm: query });
        this.openSuggestionModal(query);
      }
    } catch (error) {
      console.error('Error fetching search suggestions:', error);
    }
  });

  handleOnSearch = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    const characterCode = e.charCode || e.keyCode || e.key;

    // if user presses escape
    if (characterCode === 27) {
      this.clearSuggestions();
      return;
    }

    // if user presses enter
    if (characterCode !== 13) {
      // @ts-ignore
      const query = e.target.value;
      
      if (query && query.trim() !== '') {
        this.searchApi(query);
      } else {
        this.clearSuggestions();
      }
      
      this.setState({ searchTerm: query });
    }
  };

  clearSuggestions = () => {
    this.setState({ items: [], showSuggestions: false });
  };
  
  // Show suggestions directly below the search box without using the modal system
  openSuggestionModal = (query: string) => {
    // Show suggestions
    this.setState({ showSuggestions: true });
  };
  
  // Handle clicks outside the search container
  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
  }
  
  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
  
  handleClickOutside = (event: MouseEvent) => {
    if (this.searchContainerRef.current && 
        !this.searchContainerRef.current.contains(event.target as Node)) {
      this.clearSuggestions();
    }
  }

  // Render a search suggestion item
  renderSuggestionItem = (item: { id: string; name: string; type?: string; image?: string }) => {
    const query = this.state.searchTerm;
    
    // Highlight matching text
    const highlightMatch = (text: string, query: string) => {
      if (!query) return text;
      
      const regex = new RegExp(`(${query})`, 'gi');
      const parts = text.split(regex);
      
      return (
        <>
          {parts.map((part, i) => 
            regex.test(part) ? <span key={i} className="u-color-magenta u-semi-bold">{part}</span> : part
          )}
        </>
      );
    };
    
    return (
      <div
        key={item.id}
        className="u-flex u-align-center u-w-all u-hoverover--darker-background u-cursor--pointer u-pv--12 u-ph--16"
        onClick={() => this.submitSearchTerm(item.name)}
      >
        {item.image && (
          <div className="u-mr--12" style={{ width: "32px", height: "32px", flexShrink: 0 }}>
            <img 
              src={item.image} 
              alt={item.name} 
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
        )}
        {item.type === 'collection' && (
          <div className="u-mr--12" style={{ width: "24px", height: "24px", flexShrink: 0 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {/* Shopping basket icon */}
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <path d="M3 6h18" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
          </div>
        )}
        <div className="u-flex-grow">
          <div className="u-main-font--small">
            {highlightMatch(item.name, this.state.searchTerm)}
          </div>
          {item.type && (
            <div className="u-main-font--vsmall u-color-darkgrey">
              {item.type === 'product' ? 'Product' : 
               item.type === 'collection' ? 'Category' : 'Search'}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Group items by type
  groupItemsByType = () => {
    const { items } = this.state;
    const products = items.filter(item => item.type === 'product');
    const collections = items.filter(item => item.type === 'collection');
    const queries = items.filter(item => item.type === 'query');
    
    return { products, collections, queries };
  };
  
  // connect to redux search value
  render() {
    const { showSuggestions } = this.state;
    const { products, collections, queries } = this.groupItemsByType();
    
    return (
      <div
        ref={this.searchContainerRef}
        style={{
          marginTop: this.props.marginTop, // the distance to overlay this on the screen
          position: 'relative', // Important for positioning suggestions
        }}
      >
        <div className="u-flex u-justify-between u-align-center u-background--white">
          <div className="u-w-all">
            <input
              autoFocus
              className="u-w-all u-pv--20 u-ph--32 u-flex u-align-center u-main-font--small"
              type="text"
              placeholder="Search products"
              onKeyUp={(e) => this.handleOnSearch(e)}
              onKeyDown={(e) => this.onKeyPress(e)}
              value={this.state.searchTerm}
              onChange={(e) => this.setState({ searchTerm: e.target.value })}
            />
          </div>
          <div className="u-ph--16">
            <button
              className="u-br--small u-background--magenta u-pv--8 u-ph--20 u-color-white u-main-font--small"
              onClick={() => this.submitSearchTerm()}
            >
              Search
            </button>
          </div>
        </div>
        
        {/* Search suggestions shown directly below the search box */}
        {showSuggestions && (
          <div 
            ref={this.suggestionsRef}
            className="mobile-search-suggestions"
            style={{
              position: 'absolute',
              top: '100%', // Position right below the search box
              left: 0,
              right: 0,
              width: '100%',
              zIndex: 1000,
              backgroundColor: 'white',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
              maxHeight: '70vh', // Limit height to 70% of viewport height
              overflowY: 'auto', // Enable vertical scrolling
              WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
            }}
          >
            {/* Products section */}
            {products.length > 0 && (
              <div className="u-border-bottom--charcoal--100">
                <div className="u-main-font--small u-color-darkgrey u-ph--16 u-pt--16 u-pb--8 u-semi-bold">
                  Products
                </div>
                <div>
                  {products.slice(0, 4).map(this.renderSuggestionItem)}
                </div>
              </div>
            )}
            
            {/* Collections section */}
            {collections.length > 0 && (
              <div className="u-border-bottom--charcoal--100">
                <div className="u-main-font--small u-color-darkgrey u-ph--16 u-pt--16 u-pb--8 u-semi-bold">
                  Categories
                </div>
                <div>
                  {collections.slice(0, 3).map(this.renderSuggestionItem)}
                </div>
              </div>
            )}
            
            {/* Popular searches section */}
            {queries.length > 0 && (
              <div>
                <div className="u-main-font--small u-color-darkgrey u-ph--16 u-pt--16 u-pb--8 u-semi-bold">
                  Popular Searches
                </div>
                <div className="u-flex u-flex-wrap u-ph--12 u-pb--16">
                  {queries.slice(0, 5).map(item => (
                    <div
                      className="u-cursor--pointer u-mr--8 u-mb--8 u-br--pill u-background--lightgrey u-pv--4 u-ph--12"
                      key={item.id}
                      onClick={() => this.submitSearchTerm(item.name)}
                    >
                      <div className="u-main-font--small u-flex u-align-center">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" 
                          style={{ width: "14px", height: "14px", marginRight: "4px" }}>
                          <circle cx="11" cy="11" r="8" />
                          <path d="M21 21l-4.35-4.35" />
                        </svg>
                        {this.renderSuggestionItem(item)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* No results message */}
            {products.length === 0 && collections.length === 0 && queries.length === 0 && (
              <div className="u-pv--24 u-ph--16 u-text-align--center u-color-darkgrey">
                <div className="u-mb--12">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" 
                    style={{ width: "48px", height: "48px", margin: "0 auto" }}>
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                    <path d="M8 11h6" />
                  </svg>
                </div>
                <div className="u-main-font--medium">No results found</div>
                <div className="u-main-font--small u-mt--4">Try a different search term</div>
              </div>
            )}
            
            {/* View all results button */}
            {(products.length > 0 || collections.length > 0 || queries.length > 0) && (
              <div 
                className="u-cursor--pointer u-pv--12 u-ph--16 u-text-align--center u-background--lightgrey u-color-magenta u-semi-bold u-main-font--small"
                onClick={() => this.submitSearchTerm()}
              >
                View all results
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  search: state.search
});

export default connect(mapStateToProps, { 
  saveNewSearch, 
  hideModal, 
  saveNewCategoryFilterSelection,
  toggleHalfThePriceProducts
})(NavBarSearchMobile);
